import styled from '@emotion/styled'
import { authApi } from '@mathflat/domain/Auth'
import * as CryptoJS from 'crypto-js'
import { format } from 'date-fns'
import { useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'

import { LOGOUT_ALERT_MODAL_ORDERING_NUMBER } from '~/@common/constants/ui/modalOrdering'
import authService from '~/@common/services/auth.service'
import modalService from '~/@common/services/modal.service'
import { localStorageService } from '~/@common/services/storage.service'
import validationService from '~/@common/services/validation.service'
import { colors, typo } from '~/@common/styles'
import Button from '~/@common/ui/(Button)/Button/Button'
import Modal from '~/@common/ui/modal/Modal'
import { ValidationInput } from '~/@common/ui/ValidationInput'
import { envUtils } from '~/@common/utils/envUtils'

import LogoutAfterPasswordChangeModal from './LogoutAfterPasswordChangeModal'

const ForcePasswordChangeModal = () => {
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    formState: { errors },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      newPassword: '',
      newPasswordCheck: '',
    },
  })

  const onSubmit: SubmitHandler<{
    newPassword: string
    newPasswordCheck: string
  }> = async (data) => {
    if (data.newPassword !== data.newPasswordCheck) {
      setError('newPasswordCheck', {
        message: '비밀번호가 일치하지 않습니다.',
      })
      return
    }

    setIsLoading(true)

    try {
      const bodyString = JSON.stringify(data)
      const timestamp = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss")

      const bodyMessage = `${bodyString}:${timestamp}`

      const key = CryptoJS.enc.Utf8.parse(envUtils.cryptoKey)
      const message = CryptoJS.enc.Utf8.parse(bodyMessage)

      const hmac = CryptoJS.HmacSHA256(message, key)
      const hashBase64 = CryptoJS.enc.Base64.stringify(hmac)

      await authApi.forceChangePassword(data, {
        requestDatetime: timestamp,
        authSignature: hashBase64,
      })

      // 비밀번호 변경 후에 isPasswordChangedRequired값 업데이트하여 historypage에 진입 시 다시 비밀번호 모달 뜨지 않도록
      localStorageService.clearPasswordChangeRequired()
      modalService.closeModal()
      modalService.openModal(
        <LogoutAfterPasswordChangeModal
          onConfirm={() => {
            authService.logout()
            modalService.closeModal()
          }}
        />,
        {
          modalName: '비밀번호 변경 후 로그아웃 모달',
          onAfterClose: () => {
            authService.logout()
            return true
          },
        },
      )
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = () => {
    modalService.openModal(
      <Modal.Confirm.Negative
        cancel={{ children: '취소' }}
        confirm={{
          onClick: () => {
            authService.logout()
            modalService.closeModal()
          },
          children: '로그아웃',
        }}
      >
        로그아웃 하시겠습니까?
      </Modal.Confirm.Negative>,
      { modalName: '로그아웃 모달', orderingNumber: LOGOUT_ALERT_MODAL_ORDERING_NUMBER },
    )
  }
  return (
    <S.Container>
      <div className="description">
        영문 대/소문자, 숫자, 특수문자 중 2가지를
        <br />
        조합하여 6자 이상으로 만들어 주세요.
        <br /> S 아이디(혹은 P 아이디)의 비밀번호도 함께 변경됩니다.
        <br />
        <br />
        보안을 위해 로그인된 모든 기기에서 자동으로 로그아웃됩니다. <br />
        변경 후 새 비밀번호로 다시 로그인해 주세요.
      </div>
      <div className="form">
        <div className="form-item">
          <div className="form-item-title">새로운 비밀번호</div>
          <ValidationInput
            className="form-item-input"
            type="password"
            placeholder="새로운 비밀번호를 입력해주세요."
            {...register('newPassword', {
              required: validationService.requiredMessage,
              validate: validationService.password,
            })}
            errorMessage={errors.newPassword?.message}
          />
        </div>
        <div className="form-item">
          <div className="form-item-title">비밀번호 확인</div>
          <ValidationInput
            type="password"
            className="form-item-input"
            placeholder="다시 한 번 입력해주세요"
            errorMessage={errors.newPasswordCheck?.message}
            {...register('newPasswordCheck', {
              required: validationService.requiredMessage,
            })}
          />
        </div>
      </div>
      <Button
        size="large"
        theme="primary"
        style={{ borderRadius: '8px', width: '100%' }}
        onClick={handleSubmit(onSubmit)}
        isLoading={isLoading}
      >
        변경 완료
      </Button>
      <button className="logout-button" onClick={handleLogout}>
        로그아웃
      </button>
    </S.Container>
  )
}

export default ForcePasswordChangeModal

export const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: clamp(267px, 90vw, 444px);
    height: 100%;
    padding: 0 30px 30px;

    .title {
      ${typo.body01};
      color: ${colors.gray.$900};
      font-weight: 700;
      margin-bottom: 20px;
    }

    .description {
      ${typo.caption01};
      display: flex;
      width: 100%;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: ${colors.gray.$100};
      border-radius: 12px;
      padding: 13px 16px;
      color: ${colors.gray.$700};
      text-align: center;
      word-break: keep-all;
    }

    .form {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: 24px;
      margin: 26px 0;

      .form-item {
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      .form-item-title {
        ${typo.body02};
        margin-bottom: 14px;
        color: ${colors.gray.$900};
        font-weight: 700;
      }

      .form-item-input {
        width: 100%;
      }
    }

    .button-container {
      display: flex;
      width: 100%;
      gap: 10px;
    }

    .logout-button {
      ${typo.caption01};
      margin-top: 20px;
      color: ${colors.gray.$700};
      text-decoration: underline;
      cursor: pointer;
    }
  `,
}
